import type { ReactNode } from 'react';

import { cn } from '@/lib/utils/cn';

/**
 * Section d'un formulaire long.
 *
 * Même habillage que `DetailSection` de la fiche candidat — bordure, en-tête
 * teinté, ombre légère — pour qu'une page de saisie et une page de lecture se
 * reconnaissent comme appartenant au même produit.
 *
 * Une page longue et non des onglets : une création se remplit de haut en bas,
 * et des onglets cacheraient des champs obligatoires derrière un clic. Sur une
 * modification, l'utilisateur sait déjà où aller.
 *
 * Partagé par les besoins, les sociétés et les contacts : trois formulaires de
 * plus de dix champs, qui doivent se ressembler.
 */
export const FormSection = ({
  title,
  description,
  icon,
  children,
  className,
}: {
  title: string;
  description?: string;
  icon?: ReactNode;
  children: ReactNode;
  className?: string;
}) => (
  <section
    className={cn(
      'border-navy-200/70 overflow-hidden rounded-[10px] border bg-white shadow-[inset_0_1px_0_0_rgb(255_255_255/0.95),0_4px_16px_-8px_rgb(15_42_95/0.1)]',
      className,
    )}
  >
    <div className="border-navy-100 bg-navy-50/40 flex items-center gap-3 border-b bg-linear-to-b from-white/90 to-transparent px-5 py-4">
      {icon ? (
        <span
          aria-hidden
          className="border-navy-200/60 text-navy-500 grid size-8 shrink-0 place-items-center rounded-lg border bg-white text-base shadow-[inset_0_1px_0_0_white]"
        >
          {icon}
        </span>
      ) : null}

      <div className="min-w-0">
        <h2 className="text-navy-900 text-[14px] font-semibold">{title}</h2>
        {description ? <p className="text-ink-500 mt-0.5 text-xs">{description}</p> : null}
      </div>
    </div>

    <div className="p-5">{children}</div>
  </section>
);

/** Grille à deux colonnes, qui retombe sur une seule en dessous de `sm`. */
export const FormGrid = ({ children }: { children: ReactNode }) => (
  <div className="grid grid-cols-1 gap-x-5 gap-y-4 sm:grid-cols-2">{children}</div>
);

/** Champ occupant toute la largeur de la grille — description, commentaire. */
export const FormFull = ({ children }: { children: ReactNode }) => (
  <div className="sm:col-span-2">{children}</div>
);
